/**
 * Auth API Service Layer
 * All authentication-related API calls
 */
import http from '../../api/Services/HttpCommon';

const NEW_BASE_URL = process.env.REACT_APP_NEWBASEURL;

/**
 * Send OTP to mobile number
 * @param {string} mobileNumber - 10 digit mobile number
 * @returns {Promise} API response
 */
export async function sendMobileOtp(mobileNumber) {
  try {
    const response = await http.post('patient-user/initiate', {
      mobileNumber,
      sourceChannel: 'D2C_QUANTUM',
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.details?.[0] || error.response?.data?.message || 'Failed to send OTP';
    throw new Error(message);
  }
}

/**
 * Send OTP to email
 * @param {string} email - Email address
 * @returns {Promise} API response
 */
export async function sendEmailOtp(email) {
  try {
    const response = await http.post('patient-user/authentication/email/otp', { email });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.details?.[0] || error.response?.data?.message || 'Failed to send OTP';
    throw new Error(message);
  }
}

/**
 * Verify OTP for login/signup
 * @param {Object} data - { mobileNumber?, email?, otp }
 * @returns {Promise} API response with user data
 */
export async function verifyOtp(data) {
  try {
    const response = await http.post('patient-user/authentication/validate/otp', data);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.details?.[0] || error.response?.data?.message || 'Invalid OTP';
    throw new Error(message);
  }
}

/**
 * Login with password
 * @param {Object} encryptedData - Encrypted { email, password }
 * @returns {Promise} API response with user data
 */
export async function loginWithPassword(encryptedData) {
  try {
    const response = await http.post('patient-user/authentication/login/email', encryptedData);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || 'Login failed';
    throw new Error(message);
  }
}

/**
 * Check if user has password set
 * @param {string} email - Email address
 * @returns {Promise} API response
 */


export async function checkPasswordExists(email) {
  try {
    const response = await http.post('patient-user/check-password', { email });
    return { hasPassword: true };
  } catch (error) {
    const message = error.response?.data?.message || '';

    if (message === 'User Password Not Present!') {
      return { hasPassword: false, message };
    }

    if (
      error.response?.status === 404 ||
      message.toLowerCase().includes('no patient found') ||
      message.toLowerCase().includes('not found') ||
      message.toLowerCase().includes('not registered')
    ) {
      throw new Error('no patient found');
    }

    throw new Error(message || 'Failed to check password');
  }
}

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise} API response with new user data
 */
export async function registerUser(userData) {
  try {
    const response = await http.post('patient-user/register', userData);
    const data = response.data;
    
    if (data?.data?.length > 0) {
      throw new Error(data.message || 'Registration failed');
    }
    
    return data;
  } catch (error) {
    if (error.message) throw error;
    throw new Error(error.response?.data?.message || 'Registration failed');
  }
}

/**
 * Set new password for user
 * @param {Object} encryptedData - Encrypted { patientCode, password }
 * @returns {Promise} API response
 */
export async function setPassword(encryptedData) {
  try {
    const response = await http.post('patient-user/savepassword', encryptedData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to set password');
  }
}

/**
 * Send forgot password OTP
 * @param {Object} data - { mobileNumber?, email? }
 * @returns {Promise} API response with patient code
 */
export async function sendForgotPasswordOtp(data) {
  try {
    const mobileNumber = data.mobileNumber || '';
    const email = data.email || '';
    
    const response = await http.post(
      `${process.env.REACT_APP_BASEURL}PatientOTP/generateForgetPasswordOTP?mobileNumber=${mobileNumber}&email=${email}&mobileCheck=2`
    );
    return response.data;
  } catch (error) {
    const message = error.response?.data?.details?.[0] || error.response?.data?.message || 'Failed to send OTP';
    throw new Error(message);
  }
}

/**
 * Verify forgot password OTP
 * @param {Object} data - { otp, email?, mobileNumber? }
 * @returns {Promise} API response with patientCode
 */
export async function verifyForgotPasswordOtp(data) {
  try {
    const mobileNumber = data.mobileNumber || '';
    const email = data.email || '';
    const otp = data.otp || '';
    
    const response = await http.post(
      `PatientOTP/forgetPassValidation?patientOTP=${otp}&patientEmail=${email}&patientMobileNumber=${mobileNumber}`
    );
    return response.data;
  } catch (error) {
    const message = error.response?.data?.details?.[0] || error.response?.data?.message || 'Invalid OTP';
    throw new Error(message);
  }
}

/**
 * Reset password after OTP verification
 * @param {Object} data - { currentPasswordVal, patientCode }
 * @returns {Promise} API response
 */
export async function resetPassword(data) {
  try {
    const response = await http.post('PatientPasswordConfig/resetpassword/', data);
    return response.data;
  } catch (error) {
    const message = error.response?.data?.details?.[0] || error.response?.data?.message || 'Failed to reset password';
    throw new Error(message);
  }
}

/**
 * Submit user consent
 * @param {Object} consentData - Consent payload
 * @returns {Promise} API response
 */
export async function submitConsent(consentData) {
  try {
    const response = await http.post(`${NEW_BASE_URL}PatientConsent/addPatientConsent`, consentData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to submit consent');
  }
}

/**
 * Get consent versions
 * @returns {Promise} API response with consent versions
 */
export async function getConsentVersions() {
  try {
    const response = await http.get(`${NEW_BASE_URL}ConsentVersion/list/filter?userType=PATIENT_B2C`);
    const data = response.data;
    
    return {
      privacyPdf: data?.data?.[1]?.consentFile || '',
      privacyVersion: data?.data?.[1]?.versionNumber || '',
      tcPdf: data?.data?.[0]?.consentFile || '',
      tcVersion: data?.data?.[0]?.versionNumber || '',
    };
  } catch (error) {
    throw new Error('Failed to fetch consent versions');
  }
}

/**
 * Get all states for registration
 * @returns {Promise} API response with states list
 */
export async function getStates() {
  try {
    const response = await http.get('state/list');
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch states');
  }
}

/**
 * Logout user
 * @param {Object} data - { sessionId, userCode }
 * @returns {Promise} API response
 */
export async function logout(data) {
  try {
    const response = await http.post('patient-user/logout', data);
    return response.data;
  } catch (error) {
    // Silently handle logout errors
    return { success: false };
  }
}
